import React, { useState, useRef, useEffect } from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import classNames from 'classnames';

/**
 * Componente para la entrada de texto del chat
 * @param {Object} props - Propiedades del componente
 * @param {Function} props.onSendMessage - Callback para enviar mensaje
 * @param {boolean} props.disabled - Si el input está deshabilitado
 * @param {string} props.placeholder - Placeholder del input
 */
const ChatInput = ({
  onSendMessage,
  disabled = false,
  placeholder = ''
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const textareaRef = useRef(null);

  // Manejar cambio en el textarea
  const handleInputChange = (e) => {
    const value = e.target.value;
    setMessage(value);
    
    // Auto-resize del textarea
    adjustTextareaHeight();
  };

  // Ajustar altura del textarea
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 100) + 'px';
    }
  };

  // Manejar envío de mensaje
  const handleSendMessage = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
      
      // Reset altura del textarea
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  // Manejar teclas
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Focus automático
  useEffect(() => {
    if (textareaRef.current && !disabled) {
      textareaRef.current.focus();
    }
  }, [disabled]);

  const inputClasses = classNames('chat-input', {
    'chat-input--disabled': disabled
  });

  return (
    <div className={inputClasses}>
      <div className="chat-input-wrapper">
        {/* Textarea para el mensaje */}
        <div className="chat-input-field">
          <InputTextarea
            ref={textareaRef}
            value={message}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder || t('chat.escribe_mensaje')}
            disabled={disabled}
            autoResize={false}
            rows={1}
            className="chat-textarea"
          />
        </div>

        {/* Botones adicionales */}
        <div className="chat-input-actions">
          {/* Botón de envío */}
          <Button
            icon={<Send size={16} />}
            className={`chat-send-button ${!message.trim() ? 'chat-send-button--disabled' : 'chat-send-button--active'}`}
            onClick={handleSendMessage}
            disabled={disabled || !message.trim()}
            tooltip={t('chat.enviar')}
            tooltipOptions={{ position: 'top' }}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
